import { get, set } from "idb-keyval";
import type { QueuedMutation } from "@training/shared";

/**
 * Mutations parked on the phone while the server is unreachable.
 *
 * Gyms eat signal and Tailscale drops; a set typed between two series must
 * survive that. Every mutation carries a clientId and the server upserts on it,
 * so draining the queue twice is harmless and order only matters for readability.
 */

const KEY = "training:mutation-queue";

export type QueueItem = QueuedMutation & { queuedAt: string; attempts: number };

let memory: QueueItem[] | null = null;
const listeners = new Set<(items: QueueItem[]) => void>();

async function load(): Promise<QueueItem[]> {
  if (memory) return memory;
  // Private mode or blocked storage: fall back to memory rather than crash.
  memory = (await get<QueueItem[]>(KEY).catch(() => undefined)) ?? [];
  return memory;
}

async function save(items: QueueItem[]): Promise<void> {
  memory = items;
  listeners.forEach((listener) => listener(items));
  await set(KEY, items).catch(() => {
    /* memory-only for this session */
  });
}

export async function enqueue(mutation: QueuedMutation): Promise<void> {
  const items = await load();
  // Re-editing a set that has not been sent yet replaces the pending entry
  // instead of stacking a second one.
  const index = items.findIndex((item) => item.kind === mutation.kind && item.clientId === mutation.clientId);
  const entry: QueueItem = { ...mutation, queuedAt: new Date().toISOString(), attempts: 0 };
  if (index >= 0) items[index] = entry;
  else items.push(entry);
  await save([...items]);
}

export async function peek(): Promise<QueueItem[]> {
  return [...(await load())];
}

export async function size(): Promise<number> {
  return (await load()).length;
}

export async function remove(clientIds: string[]): Promise<void> {
  const drop = new Set(clientIds);
  const items = await load();
  await save(items.filter((item) => !drop.has(item.clientId)));
}

export async function clear(): Promise<void> {
  await save([]);
}

export function subscribe(listener: (items: QueueItem[]) => void): () => void {
  listeners.add(listener);
  void load().then((items) => listener(items));
  return () => listeners.delete(listener);
}

export async function markAttempt(clientIds: string[]): Promise<void> {
  const bump = new Set(clientIds);
  const items = await load();
  await save(items.map((item) => (bump.has(item.clientId) ? { ...item, attempts: item.attempts + 1 } : item)));
}
